/**
 * T-137 (P1): recover from a stale-build chunk 404 instead of showing a user a
 * developer error wall.
 *
 * After a deploy, a returning visitor can still be holding an index.html that
 * names the PREVIOUS build's hashed chunks. Those files are gone, so the lazy
 * import rejects with "Failed to fetch dynamically imported module" and the
 * router renders its dev-facing boundary ("Unexpected Application Error!",
 * "Hey developer"). The document is simply out of date; one reload fixes it.
 *
 * THIS IS NOT A WIDER ErrorBoundary (decision 99). A boundary catches the error
 * and leaves the user sitting on a broken build with a prettier message. This
 * re-fetches the CORRECT build and continues. We act only on the specific
 * stale-import signature and let every other error surface untouched — a
 * blanket "reload on any error" would hide real bugs behind a refresh.
 *
 * RELOAD LOOP GUARD (the CEO's explicit requirement): a page that reloads
 * forever is worse than an error screen. We record the attempt in
 * sessionStorage BEFORE reloading, so if the fresh document still fails we do
 * NOT reload again — the error surfaces visibly and the loop cannot start.
 * sessionStorage (not localStorage) so the guard dies with the tab and a user
 * returning tomorrow still gets their one automatic recovery.
 */

const GUARD_KEY = 'mw:stale-chunk-reloaded';

/**
 * The signature of a chunk that no longer exists on the server. Browsers word
 * this differently, so match on all of them rather than one engine's string.
 */
export function isStaleChunkError(error: unknown): boolean {
  const message =
    typeof error === 'string'
      ? error
      : error instanceof Error
        ? `${error.name}: ${error.message}`
        : typeof (error as { message?: unknown })?.message === 'string'
          ? String((error as { message: string }).message)
          : '';

  if (!message) return false;

  return (
    // Chrome/Edge
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    // Firefox
    message.includes('error loading dynamically imported module') ||
    // Safari
    message.includes('Importing a module script failed') ||
    // Vite's own preload helper
    message.includes('Unable to preload CSS') ||
    message.includes('dynamically imported module')
  );
}

/** True when we have already spent this tab's one automatic reload. */
export function hasUsedReload(storage: Storage): boolean {
  try {
    return storage.getItem(GUARD_KEY) === '1';
  } catch {
    // Private mode / storage disabled: treat as "already used" so we can never
    // loop. Failing visibly beats reloading forever.
    return true;
  }
}

/** Clear the guard once the app has successfully booted on the new build. */
export function clearReloadGuard(storage: Storage): void {
  try {
    storage.removeItem(GUARD_KEY);
  } catch {
    /* storage unavailable — nothing to clear */
  }
}

/**
 * Decide what to do with an error. Returns true when the caller reloaded.
 * Injectable storage/reload so this is testable without a real browser.
 */
export function recoverFromStaleChunk(
  error: unknown,
  storage: Storage,
  reload: () => void
): boolean {
  if (!isStaleChunkError(error)) return false;
  if (hasUsedReload(storage)) return false;

  try {
    // Set the guard BEFORE reloading. If this throws we must not reload at all.
    storage.setItem(GUARD_KEY, '1');
  } catch {
    return false;
  }

  reload();
  return true;
}

/**
 * Wire up global listeners. A failed lazy import surfaces as an unhandled
 * rejection (router loaders) or a window error, so we listen to both.
 */
export function installStaleChunkRecovery(): void {
  if (typeof window === 'undefined') return;

  const attempt = (error: unknown) =>
    recoverFromStaleChunk(error, window.sessionStorage, () => window.location.reload());

  window.addEventListener('unhandledrejection', (event) => {
    attempt(event.reason);
  });

  window.addEventListener('error', (event) => {
    attempt(event.error ?? event.message);
  });

  // We got here, so this build's entry chunk loaded: the app is healthy and the
  // next stale deploy deserves a fresh reload allowance.
  clearReloadGuard(window.sessionStorage);
}
